import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";

export interface DepartmentRecord {
  id: string;
  companyId?: string | null;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export type DepartmentDraft = Pick<DepartmentRecord, "name" | "description">;

const LS_KEY = "inventory.departments.v1";
const BOOT_KEY = "inventory.departments.demoSeeded.v1";

/** Demo rows when the API is unavailable or returns no data (matches employee screen samples). */
const STATIC_DEMO_DEPARTMENTS: DepartmentRecord[] = (() => {
  const t = "2023-01-01T00:00:00.000Z";
  const now = new Date().toISOString();
  return [
    {
      id: "dept-demo-mgmt",
      name: "Management",
      description: "Executive leadership and strategic planning.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "dept-demo-sales",
      name: "Sales",
      description: "Sales, business development, and customer acquisition.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "dept-demo-ops",
      name: "Operations",
      description: "Day-to-day operations and service delivery.",
      createdAt: t,
      updatedAt: now,
    },
    {
      id: "dept-demo-hr",
      name: "Human Resources",
      description: "HR, payroll, policies, and recruitment.",
      createdAt: t,
      updatedAt: now,
    },
  ];
})();

type DepartmentApiRecord = {
  id?: number | string;
  company_id?: string | number | null;
  name: string;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UpsertDepartmentApiResponse = {
  message?: string;
  data?: DepartmentApiRecord;
};

type DeleteDepartmentApiResponse = {
  message?: string;
};

type UpsertDepartmentResult = {
  record: DepartmentRecord;
  message: string;
};

type DeleteDepartmentResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: DepartmentRecord[]): DepartmentRecord[] {
  const byId = new Map<ID, DepartmentRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): DepartmentApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): DepartmentApiRecord[] | null => {
    if (Array.isArray(block)) return block as DepartmentApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as DepartmentApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "departments", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractDepartmentFromUpsertBody(body: unknown): DepartmentApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as DepartmentApiRecord;
    }
    const nested = block.department;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as DepartmentApiRecord;
    }
  }
  const topDept = root.department;
  if (topDept && typeof topDept === "object" && !Array.isArray(topDept)) {
    return topDept as unknown as DepartmentApiRecord;
  }
  if (typeof root.name === "string" && "id" in root) {
    return root as unknown as DepartmentApiRecord;
  }
  return null;
}

function mapApiRecord(record: DepartmentApiRecord): DepartmentRecord {
  const created =
    record.created_at || record.createdAt || new Date().toISOString();
  const updated = record.updated_at || record.updatedAt || created;
  return {
    id: String(record.id ?? ""),
    companyId: record.company_id != null ? String(record.company_id) : null,
    name: record.name,
    description: (record.description ?? "").toString(),
    createdAt: created,
    updatedAt: updated,
  };
}

function buildApiPayload(draft: DepartmentDraft) {
  return {
    name: draft.name.trim(),
    description: (draft.description ?? "").trim() || null,
  };
}

function load(): DepartmentRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as DepartmentRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: DepartmentRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listDepartments(): DepartmentRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getDepartmentById(id: ID): DepartmentRecord | undefined {
  return load().find((d) => d.id === id);
}

export function seedDepartmentsIfEmpty() {
  if (typeof localStorage === "undefined") return;
  if (localStorage.getItem(BOOT_KEY)) return;
  if (load().length > 0) {
    localStorage.setItem(BOOT_KEY, "1");
    return;
  }
  save(STATIC_DEMO_DEPARTMENTS);
  localStorage.setItem(BOOT_KEY, "1");
}

/** Ensures there is data to show when sync cannot load from the API. */
function ensureDepartmentsDemoFallback() {
  if (load().length > 0) return;
  save(STATIC_DEMO_DEPARTMENTS);
  if (typeof localStorage !== "undefined") localStorage.setItem(BOOT_KEY, "1");
}

const DEPARTMENTS_PER_PAGE = 20;

export async function syncDepartmentsFromApi() {
  try {
    const collected: DepartmentApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/departments", {
        params: { page, per_page: DEPARTMENTS_PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(
      collected.map(mapApiRecord).filter((r) => Boolean(r.id)),
    );
    save(records);
    return listDepartments();
  } catch (error) {
    console.error("[Departments] syncDepartmentsFromApi failed", error);
    ensureDepartmentsDemoFallback();
    return listDepartments();
  }
}

export async function createDepartment(draft: DepartmentDraft): Promise<UpsertDepartmentResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const response = await api.post<UpsertDepartmentApiResponse>("/api/v1/departments", payload);
  const created = extractDepartmentFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create department response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Department created successfully." };
}

export async function updateDepartment(id: ID, draft: DepartmentDraft): Promise<UpsertDepartmentResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft);
  const response = await api.put<UpsertDepartmentApiResponse>(`/api/v1/departments/${id}`, payload);
  const updated = extractDepartmentFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update department response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Department updated successfully." };
}

export async function deleteDepartment(id: ID): Promise<DeleteDepartmentResult> {
  const response = await api.delete<DeleteDepartmentApiResponse>(`/api/v1/departments/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Department deleted successfully." };
}
